import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  IconButton,
  Button,
  useTheme,
  alpha,
  Divider,
  Chip,
  Grid,
  Pagination,
  Stack,
  Menu,
  MenuItem,
  Tooltip,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  useMediaQuery,
} from "@mui/material";
import { useHistory } from "react-router";

// Icons
import NotificationsIcon from "@mui/icons-material/Notifications";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import MarkEmailUnreadIcon from "@mui/icons-material/MarkEmailUnread";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FilterListIcon from "@mui/icons-material/FilterList";
import BuildIcon from "@mui/icons-material/Build";
import {
  deleteNotificationById,
  getUserNotificationData,
  markNotificationById,
  getNotificationTypes,
} from "../../services/Notification";

let _notificationsState = null;

function Notifications() {
  const theme = useTheme();
  const history = useHistory();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [notifications, setNotifications] = useState([]);
  const [page, setPage] = useState(() => _notificationsState?.page ?? 1);
  const [rowsPerPage, setRowsPerPage] = useState(() => _notificationsState?.rowsPerPage ?? 10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [filter, setFilter] = useState(() => _notificationsState?.filter ?? "ALL");
  const [notificationTypes, setNotificationTypes] = useState([]);
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeNotification, setActiveNotification] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    _notificationsState = null;
  }, []);

  useEffect(() => {
    getNotificationTypes().then((types) => {
      if (Array.isArray(types)) setNotificationTypes(types);
    });
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [page, rowsPerPage, filter]);

  const buildFilterBody = (value) => {
    if (value === "ALL") return { type: null, status: null };
    if (value === "UNREAD") return { type: null, status: "UNREAD" };
    return { type: value, status: null };
  };

  const formatTypelabel = (type) =>
    type
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const pageData = await getUserNotificationData(
        page - 1,
        rowsPerPage,
        buildFilterBody(filter),
      );
      setNotifications(pageData.content || []);
      setTotalElements(pageData.totalElements || 0);
      setTotalPages(pageData.totalPages || 0);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
      setNotifications([]);
      setTotalElements(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, notification) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setActiveNotification(notification);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActiveNotification(null);
  };

  const handleDelete = async (notificationId) => {
    const isDeleted = await deleteNotificationById(notificationId);
    if (isDeleted) {
      fetchNotifications();
    }
    handleMenuClose();
  };

  const handleMarkAsRead = async (notificationId) => {
    const isMarked = await markNotificationById(notificationId, true);
    if (isMarked) {
      fetchNotifications();
    }
    handleMenuClose();
  };

  const handleMarkAsUnread = async (notificationId) => {
    const isMarked = await markNotificationById(notificationId, false);
    if (isMarked) {
      fetchNotifications();
    }
    handleMenuClose();
  };

  const handleBulkDelete = async () => {
    const selectedItems = notifications.filter((n) =>
      selectedNotifications.includes(n.userNotificationId),
    );
    await Promise.all(
      selectedItems.map((n) => deleteNotificationById(n.notificationId)),
    );
    setSelectedNotifications([]);
    fetchNotifications();
  };

  const handleBulkMarkAsRead = async () => {
    const selectedItems = notifications.filter((n) =>
      selectedNotifications.includes(n.userNotificationId),
    );
    await Promise.all(
      selectedItems.map((n) => markNotificationById(n.notificationId, true)),
    );
    setSelectedNotifications([]);
    fetchNotifications();
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedNotifications(notifications.map((n) => n.userNotificationId));
    } else {
      setSelectedNotifications([]);
    }
  };

  const handleSelectNotification = (event, notificationId) => {
    event.stopPropagation();
    setSelectedNotifications((prev) =>
      prev.includes(notificationId)
        ? prev.filter((id) => id !== notificationId)
        : [...prev, notificationId],
    );
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "LOW_STOCK":
        return <WarningIcon sx={{ color: theme.palette.warning.main }} />;
      case "ERROR":
        return <ErrorIcon sx={{ color: theme.palette.error.main }} />;
      case "APPROVE_SALE_BILL":
        return <InfoIcon sx={{ color: theme.palette.info.main }} />;
      case "SUCCESS":
        return <CheckCircleIcon sx={{ color: theme.palette.success.main }} />;
      case "IMPORT_BILL_APPROVAL":
        return <InfoIcon sx={{ color: theme.palette.warning.main }} />;
      case "SEND_IMPORT_BILL_APPROVAL":
        return <CheckCircleIcon sx={{ color: theme.palette.success.main }} />;
      case "SUB_ORDER_ITEM_DELETE":
        return <WarningIcon sx={{ color: theme.palette.error.main }} />;
      case "SUB_ORDER_ITEM_DELETE_APPROVAL":
        return <CheckCircleIcon sx={{ color: theme.palette.warning.main }} />;
      case "PRODUCTION_SEND_APPROVAL":
        return <BuildIcon sx={{ color: theme.palette.warning.main }} />;
      case "PRODUCTION_APPROVAL":
        return <BuildIcon sx={{ color: theme.palette.success.main }} />;
      default:
        return <InfoIcon sx={{ color: theme.palette.info.main }} />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case "LOW_STOCK":
        return theme.palette.warning.main;
      case "ERROR":
        return theme.palette.error.main;
      case "APPROVE_SALE_BILL":
        return theme.palette.info.main;
      case "SUCCESS":
        return theme.palette.success.main;
      case "IMPORT_BILL_APPROVAL":
        return theme.palette.warning.main;
      case "SEND_IMPORT_BILL_APPROVAL":
        return theme.palette.success.main;
      case "SUB_ORDER_ITEM_DELETE":
        return theme.palette.error.main;
      case "SUB_ORDER_ITEM_DELETE_APPROVAL":
        return theme.palette.warning.main;
      case "PRODUCTION_SEND_APPROVAL":
        return theme.palette.warning.main;
      case "PRODUCTION_APPROVAL":
        return theme.palette.success.main;
      default:
        return theme.palette.info.main;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now - date;
    const diffInMinutes = Math.floor(diffInMs / 60000);
    const diffInHours = Math.floor(diffInMs / 3600000);
    const diffInDays = Math.floor(diffInMs / 86400000);

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  };

  const navigateTo = (path) => {
    _notificationsState = { page, rowsPerPage, filter };
    history.push(path);
  };

  const redirectPage = (notification) => {
    switch (notification.type) {
      case "LOW_STOCK":
        navigateTo("/template/im_item_view/" + notification.referenceId);
        break;
      case "ERROR":
        navigateTo("/logs");
        break;
      case "APPROVE_SALE_BILL":
        navigateTo("/template/sale_bill_view/" + notification.referenceId);
        break;
      case "SUBORDER_PAYMENT":
        navigateTo("/template/sub_order_view/" + notification.referenceId);
        break;
      case "APPROVE_SUBORDER_PAYMENT":
        navigateTo("/template/sub_order_view/" + notification.referenceId);
        break;
      case "SUBORDER_BULK_PAYMENT":
        navigateTo("/template/sub_order_person_view/" + notification.referenceId + "?tab=1");
        break;
      case "APPROVE_SUBORDER_BULK_PAYMENT":
        navigateTo("/template/sub_order_person_view/" + notification.referenceId + "?tab=1");
        break;
      case "SUBORDER_APPROVAL":
        navigateTo("/template/sub_order_view/" + notification.referenceId);
        break;
      case "IMPORT_BILL_APPROVAL":
        navigateTo("/template/im_bill_view/" + notification.referenceId);
        break;
      case "SEND_IMPORT_BILL_APPROVAL":
        navigateTo("/template/im_bill_view/" + notification.referenceId);
        break;
      case "SUB_ORDER_ITEM_DELETE":
        navigateTo("/template/sub_order_view/" + notification.referenceId);
        break;
      case "SUB_ORDER_ITEM_DELETE_APPROVAL":
        navigateTo("/template/sub_order_view/" + notification.referenceId);
        break;
      case "PRODUCTION_SEND_APPROVAL":
        navigateTo("/template/ex_production_view/" + notification.referenceId);
        break;
      case "PRODUCTION_APPROVAL":
        navigateTo("/template/ex_production_view/" + notification.referenceId);
        break;
      default:
        break;
    }
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    setSelectedNotifications([]);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(event.target.value);
    setPage(1);
    setSelectedNotifications([]);
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, sm: 2, md: 3 },
        m: { xs: 0, sm: 1, md: 2 },
        borderRadius: { xs: 0, sm: 2 },
        bgcolor: "#f9fafb",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1, sm: 0 },
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
          <NotificationsIcon
            fontSize={isMobile ? "medium" : "large"}
            color="primary"
            sx={{ mr: 1.5 }}
          />
          <Box>
            <Typography
              variant={isMobile ? "h6" : "h5"}
              fontWeight="600"
              color="primary.main"
            >
              Notifications
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your system notifications
            </Typography>
          </Box>
        </Box>
        <Chip
          label={`${unreadCount} Unread`}
          color="primary"
          size={isMobile ? "small" : "medium"}
          sx={{
            fontWeight: 600,
            alignSelf: { xs: "flex-start", sm: "center" },
          }}
        />
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Filter Row */}
      <Box sx={{ mb: 2 }}>
        <FormControl fullWidth size="small">
          <InputLabel>Filter</InputLabel>
          <Select
            value={filter}
            label="Filter"
            onChange={(e) => {
              setFilter(e.target.value);
              setPage(1);
              setSelectedNotifications([]);
            }}
            startAdornment={<FilterListIcon sx={{ mr: 1, ml: 1 }} />}
          >
            <MenuItem value="ALL">All Notifications</MenuItem>
            <MenuItem value="UNREAD">Unread Only</MenuItem>
            {notificationTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {formatTypelabel(type)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Bulk Actions — shown when items selected */}
      {selectedNotifications.length > 0 && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 1.5,
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Chip
            label={`${selectedNotifications.length} selected`}
            onDelete={() => setSelectedNotifications([])}
            color="primary"
            variant="outlined"
            size="small"
          />
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="outlined"
            startIcon={<MarkEmailReadIcon />}
            onClick={handleBulkMarkAsRead}
            size="small"
          >
            {isMobile ? "Read" : "Mark as Read"}
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleBulkDelete}
            size="small"
          >
            Delete
          </Button>
        </Box>
      )}

      {/* Select All */}
      {notifications.length > 0 && (
        <Box sx={{ mb: 1, display: "flex", alignItems: "center" }}>
          <Checkbox
            checked={
              selectedNotifications.length === notifications.length &&
              notifications.length > 0
            }
            indeterminate={
              selectedNotifications.length > 0 &&
              selectedNotifications.length < notifications.length
            }
            onChange={handleSelectAll}
            size="small"
          />
          <Typography variant="body2" color="text.secondary">
            Select All
          </Typography>
        </Box>
      )}

      {/* Notifications List */}
      <Stack spacing={1.5}>
        {notifications.map((notification) => (
          <Card
            key={notification.userNotificationId}
            elevation={notification.read ? 0 : 1}
            onClick={() => redirectPage(notification)}
            sx={{
              borderRadius: 2,
              transition: "all 0.2s",
              bgcolor: notification.read
                ? "transparent"
                : alpha(theme.palette.primary.main, 0.02),
              borderLeft: notification.read
                ? "none"
                : `4px solid ${getNotificationColor(notification.type)}`,
              "&:hover": {
                boxShadow: 2,
                cursor: "pointer",
              },
            }}
          >
            <CardContent
              sx={{
                py: 1.5,
                px: { xs: 1.5, sm: 2 },
                "&:last-child": { pb: 1.5 },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                {/* Checkbox */}
                <Checkbox
                  checked={selectedNotifications.includes(
                    notification.userNotificationId,
                  )}
                  onChange={(e) =>
                    handleSelectNotification(e, notification.userNotificationId)
                  }
                  onClick={(e) => e.stopPropagation()}
                  sx={{ p: 0.5, mt: 0.25, flexShrink: 0 }}
                  size="small"
                />

                {/* Type Icon — desktop only */}
                {!isMobile && (
                  <Box
                    sx={{
                      p: 0.75,
                      borderRadius: 1.5,
                      display: "flex",
                      flexShrink: 0,
                      alignSelf: "flex-start",
                      mt: 0.25,
                      bgcolor: alpha(
                        getNotificationColor(notification.type),
                        0.1,
                      ),
                    }}
                  >
                    {getNotificationIcon(notification.type)}
                  </Box>
                )}

                {/* Content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {/* Chips row */}
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: 0.5,
                      mb: 0.5,
                    }}
                  >
                    <Chip
                      label={notification.type.replace(/_/g, " ")}
                      size="small"
                      sx={{
                        bgcolor: alpha(
                          getNotificationColor(notification.type),
                          0.1,
                        ),
                        color: getNotificationColor(notification.type),
                        fontWeight: 600,
                        height: 20,
                        fontSize: "0.65rem",
                      }}
                    />
                    {!notification.read && (
                      <Chip
                        label="NEW"
                        size="small"
                        color="primary"
                        sx={{
                          fontWeight: 600,
                          height: 20,
                          fontSize: "0.65rem",
                        }}
                      />
                    )}
                  </Box>

                  {/* Message */}
                  <Typography
                    variant="body2"
                    color="text.primary"
                    fontWeight={notification.read ? 400 : 600}
                    sx={{
                      wordBreak: "break-word",
                      lineHeight: 1.4,
                      mb: 0.5,
                    }}
                  >
                    {notification.message}
                  </Typography>

                  {/* Meta info */}
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(notification.createdAt)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      •
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ wordBreak: "break-all" }}
                    >
                      {notification.referenceEntity} #{notification.referenceId}
                    </Typography>
                  </Box>
                </Box>

                {/* More actions */}
                <Tooltip title="More actions">
                  <IconButton
                    onClick={(e) => handleMenuOpen(e, notification)}
                    size="small"
                    sx={{ flexShrink: 0, alignSelf: "flex-start", mt: 0.25 }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {/* Empty State */}
      {notifications.length === 0 && !loading && (
        <Box sx={{ textAlign: "center", py: { xs: 5, md: 8 } }}>
          <NotificationsIcon
            sx={{
              fontSize: { xs: 56, md: 80 },
              color: theme.palette.grey[300],
              mb: 2,
            }}
          />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No notifications found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You're all caught up!
          </Typography>
        </Box>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            Loading notifications...
          </Typography>
        </Box>
      )}

      {/* Pagination */}
      {totalElements > 0 && totalPages > 1 && (
        <Box
          sx={{
            mt: 4,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: { sm: "space-between" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: 2,
          }}
        >
          {/* Rows per page */}
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Rows per page</InputLabel>
            <Select
              value={rowsPerPage}
              label="Rows per page"
              onChange={handleRowsPerPageChange}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>

          {/* Count label */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: { xs: "center", sm: "left" } }}
          >
            {`${(page - 1) * rowsPerPage + 1}–${Math.min(
              page * rowsPerPage,
              totalElements,
            )} of ${totalElements}`}
          </Typography>

          {/* Pagination controls */}
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "center", sm: "flex-end" },
            }}
          >
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
              siblingCount={isMobile ? 0 : 1}
              boundaryCount={isMobile ? 1 : 1}
              size={isMobile ? "small" : "medium"}
            />
          </Box>
        </Box>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {activeNotification && !activeNotification.read && (
          <MenuItem
            onClick={() => handleMarkAsRead(activeNotification.notificationId)}
          >
            <MarkEmailReadIcon fontSize="small" sx={{ mr: 1 }} />
            Mark as Read
          </MenuItem>
        )}
        {activeNotification && activeNotification.read && (
          <MenuItem
            onClick={() =>
              handleMarkAsUnread(activeNotification.notificationId)
            }
          >
            <MarkEmailUnreadIcon fontSize="small" sx={{ mr: 1 }} />
            Mark as Unread
          </MenuItem>
        )}
        <Divider />
        <MenuItem
          onClick={() => handleDelete(activeNotification?.notificationId)}
          sx={{ color: theme.palette.error.main }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
    </Paper>
  );
}

export default Notifications;
